import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  ConsolidatedFeature,
  ConsolidatorDevice,
  CumulatedEnergyProperties,
  EssPhotovoltaicStateProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  NumberValue,
  PhotovoltaicInsulationResistanceProperties,
  PhotovoltaicInsulationResistanceStatus,
  PhotovoltaicStatusProperties,
} from '@vi-iot/client';

import { DevicesStore, FeatureService, InstallationFeaturesStore, PvStringObject } from '@store';

import { TranslateService } from '@ngx-translate/core';
import { NO_VALUE } from '@pipes';
import { SharedUiModule } from '@shared-ui';
import { take, takeUntil } from 'rxjs/operators';
import { FeaturesModule } from 'src/app/features/features.module';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';
import { SharedDependenciesModule } from 'src/app/shared';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';

@Component({
  selector: 'app-photovoltaics-overlay',
  standalone: true,
  imports: [CommonStandaloneModule, SharedDependenciesModule, SharedUiModule, FeaturesModule],
  templateUrl: './photovoltaics-overlay.component.html',
  styleUrls: ['./photovoltaics-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotovoltaicsOverlayComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly photovoltaicInsulationResistanceStatus = PhotovoltaicInsulationResistanceStatus;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  deviceId: string;
  productionCurrentConsolidatedDeviceId: string;
  photovoltaicInstalledPeakPowerConsolidatedDeviceId: string;
  exceptionsState: string;
  basicState: string;
  photoVoltaicStrings: PvStringObject[] = [];
  peakPower: NumberValue;
  isPhotovoltaicStringPresent = false;
  NO_VALUE = NO_VALUE;

  photovoltaicStatus$: Observable<Feature<PhotovoltaicStatusProperties, never>>;
  productionCurrent$: Observable<Feature<IsNumberValueProperty, unknown>>;
  productionCurrentConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  productionCurrentConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  photovoltaicInstalledPeakPower$: Observable<Feature<IsNumberValueProperty, never>>;
  photovoltaicInstalledPeakPowerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  photovoltaicInstalledPeakPowerConsolidatedFeatures$: Observable<
    Feature<ConsolidatedFeature, unknown>
  >;
  photovoltaicInsulationResistance$: Observable<
    Feature<PhotovoltaicInsulationResistanceProperties, never>
  >;
  photovoltaicInstalledPeakPowerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  powerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  photovoltaicCumulatedEnergy$: Observable<Feature<CumulatedEnergyProperties, never>>;
  photovoltaicsStringsOrders: number[] = [2, 0, 1];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private featureService: FeatureService,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.productionCurrentConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.productionCurrentConsolidatedDeviceId = feature?.deviceId;
      });

    this.featureService
      .getConsolidatedFeatures(FeatureNames.photovoltaicInstalledPeakPowerConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.photovoltaicInstalledPeakPowerConsolidatedDeviceId = feature?.deviceId;
      });

    this.devicesStore.getSelectedDevice$.pipe(take(1)).subscribe((richDevice) => {
      this.deviceId = richDevice.id;
    });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.essPhotovoltaicState, this.deviceId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature: Feature<EssPhotovoltaicStateProperties, never>) => {
        if (feature?.properties) {
          this.photovoltaicStateValues(feature);
        }
      });

    // state
    this.photovoltaicStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicStatus,
      this.deviceId
    );

    // photovoltaic.production.current
    this.productionCurrent$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.productionCurrent,
      this.deviceId
    );

    this.productionCurrentConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.productionCurrentConsolidated
    );

    this.productionCurrentConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.productionCurrentConsolidated,
      this.deviceId,
      this.productionCurrentConsolidatedDeviceId
    );

    // photovoltaic.installedPeakPower
    this.photovoltaicInstalledPeakPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicInstalledPeakPower,
      this.deviceId
    );

    this.photovoltaicInsulationResistance$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicInsulationResistance,
      this.deviceId
    );

    this.photovoltaicInstalledPeakPowerConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicInstalledPeakPowerConsolidated,
      this.deviceId
    );

    this.photovoltaicInstalledPeakPowerConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.photovoltaicInstalledPeakPowerConsolidated
    );

    this.photovoltaicInstalledPeakPowerConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.photovoltaicInstalledPeakPowerConsolidated,
      this.deviceId,
      this.photovoltaicInstalledPeakPowerConsolidatedDeviceId
    );

    // photovoltaic.production.cumulated
    this.photovoltaicCumulatedEnergy$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicCumulatedEnergy,
      this.deviceId
    );

    this.setPhotovoltaicsObjectArray();
  }

  getPhotovoltaicsObject(pvStringId: number): Observable<PvStringObject> {
    return this.installationFeaturesStore.getPVString(pvStringId.toString());
  }

  setPhotovoltaicsObjectArray(): void {
    this.photovoltaicsStringsOrders.forEach((pvStringId, index) => {
      this.getPhotovoltaicsObject(pvStringId)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((pvStringObject) => {
          if (
            pvStringObject.voltageFeature?.properties?.value ||
            pvStringObject.electricityFeature?.properties?.value ||
            pvStringObject.powerFeature?.properties?.value
          ) {
            this.isPhotovoltaicStringPresent = true;
            this.photoVoltaicStrings[index] = pvStringObject;
          }
        });
    });
  }

  photovoltaicStateValues(feature: Feature<EssPhotovoltaicStateProperties, never>): void {
    this.exceptionsState = feature.properties?.exception?.value
      ? (feature.properties?.exception?.value)
          .map((value) => {
            const key = this.customTranslationsService.ESS_PHOTOVOLTAIC_STATES_EXCEPTION[value];

            return key ? this.translateService.instant(key) : '';
          })
          .filter((item) => item !== '')
          .join(', ')
      : '';

    this.basicState = feature.properties?.basic?.value
      ? (feature.properties?.basic?.value)
          .map((value) => {
            const key = this.customTranslationsService.ESS_PHOTOVOLTAIC_STATES_BASIC[value];

            return key ? this.translateService.instant(key) : '';
          })
          .filter((item) => item !== '')
          .join(', ')
      : '';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
